import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import {
    initStoreKit,
    isStoreKitAvailable,
    purchaseProduct,
    getCustomerInfo,
    StoreKitProduct,
} from "../services/RevenueCatService";
import { Purchases } from '@revenuecat/purchases-capacitor';
import { useAppSettings } from "../components/providers/AppSettingsProvider";
import { getPageBackgroundGradient } from "../config/theme";

type Plan = {
    id: string;
    productId?: string;
    title: string;
    subtitle: string;
    price?: string;
    originalPrice?: string;
    discountPercent?: string;
    features: string[];
    isBestValue?: boolean;
    tier?: "pro" | "platinum" | "free";
    sortOrder?: number;
    promotion?: {
        price: string;
        description: string;
    } | null;
    isAnnual?: boolean;
};

function normalizePriceString(price?: string, currencyCode?: string) {
    if (!price) return price;
    if (currencyCode === 'USD' && price.startsWith('US$')) {
        return price.replace(/^US\$/, '$');
    }
    return price;
}

interface SubscriptionViewProps {
    isEmbedded?: boolean;
    onContinue?: () => void; // Called for "Continue Free" or after successful restore/purchase if handled externally
    onBack?: () => void;
}

export const SubscriptionView: React.FC<SubscriptionViewProps> = ({
    isEmbedded = false,
    onContinue,
    onBack
}) => {
    const { t, theme } = useAppSettings();
    const navigate = useNavigate();
    const isDark = theme === 'dark';
    const [iapProducts, setIapProducts] = useState<StoreKitProduct[]>([]);
    const [iapReady, setIapReady] = useState(false);
    const [iapStatus, setIapStatus] = useState<string | null>(null);
    const [iapError, setIapError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState<string>("free");
    const [selectedTier, setSelectedTier] = useState<"pro" | "platinum">("pro");
    const [activeSubscriptionProductId, setActiveSubscriptionProductId] = useState<string | null>(null);
    const [subscriptionManagementURL, setSubscriptionManagementURL] = useState<string | null>(null);

    // Load IAP mechanism
    useEffect(() => {
        let cancelled = false;
        const loadStoreKit = async () => {
            setIapStatus(t("purchase.loadingOptions"));

            const result = await initStoreKit();

            if (cancelled) return;

            if (!result.available) {
                setIapReady(false);
                setIapStatus(null);
                setIapError(result.reason || t("purchase.unavailable"));
                return;
            }

            setIapProducts(result.products);
            setIapReady(true);
            setIapError(null);
            setIapStatus(null);

            try {
                const customerInfo = await getCustomerInfo();
                if (cancelled || !customerInfo) return;

                const managementURL = (customerInfo as any)?.managementURL as string | undefined;
                if (managementURL) {
                    setSubscriptionManagementURL(managementURL);
                }

                const entitlements = (customerInfo as any)?.entitlements?.active;
                if (entitlements && Object.keys(entitlements).length > 0) {
                    const activeEntValues = Object.values(entitlements) as any[];
                    const activeEnt = activeEntValues[0];
                    if (activeEnt?.productIdentifier) {
                        setActiveSubscriptionProductId(activeEnt.productIdentifier);
                    }
                }
            } catch (error) {
                console.error("[Purchase] Failed to check active subscription", error);
            }
        };

        if (isStoreKitAvailable()) {
            loadStoreKit();
        } else {
            setIapReady(false);
            setIapError(t("purchase.nativeRequired"));
            setIapStatus(t("purchase.nativeHint"));
        }

        return () => {
            cancelled = true;
        };
    }, [t]);

    // Build Plan List dynamically
    const plans = useMemo<Plan[]>(() => {
        const tierFeatures = {
            free: [
                t("purchase.features.free.0"),
                t("purchase.features.free.1"),
            ],
            pro: [
                t("purchase.features.pro.0"),
                t("purchase.features.pro.1"),
                t("purchase.features.pro.2"),
            ],
            platinum: [
                t("purchase.features.platinum.0"),
                t("purchase.features.platinum.1"),
                t("purchase.features.platinum.2"),
                t("purchase.features.platinum.3"),
            ],
        };

        const freePlan: Plan = {
            id: "free",
            title: t("purchase.freeTitle"),
            subtitle: activeSubscriptionProductId ? "" : t("purchase.freeCurrent"),
            price: t("purchase.freePrice"),
            features: tierFeatures.free,
            tier: "free",
            sortOrder: 0,
            isAnnual: false,
        };

        const paidPlans: Plan[] = iapProducts.map((p) => {
            const pkgId = p.rcPackageIdentifier || "";
            const isPlatinum = pkgId.includes("platinum");
            const isAnnual = pkgId.includes("annual");

            let title = isPlatinum ? t("purchase.plan.platinum") : t("purchase.plan.pro");
            let tier: "pro" | "platinum" = isPlatinum ? "platinum" : "pro";

            if (isAnnual) title += ` ${t("purchase.plan.annualLabel")}`;
            else title += ` ${t("purchase.plan.monthlyLabel")}`;

            let promotion: { price: string; description: string } | null = null;
            if (p.introPrice) {
                const intro = p.introPrice;
                const unit = intro.periodUnit.toLowerCase();
                const periodText =
                    intro.periodNumberOfUnits === 1
                        ? t(`purchase.period.${unit}.one`)
                        : t(`purchase.period.${unit}.other`, { count: intro.periodNumberOfUnits });

                let description = "";
                if (intro.price === 0) {
                    description = t("purchase.promo.freeTrial", { period: periodText });
                } else {
                    description = t("purchase.promo.introPrice", { price: intro.priceString, period: periodText });
                }

                promotion = {
                    price: intro.priceString,
                    description: description,
                };
            }

            let originalPrice: string | undefined;
            let discountPercent: string | undefined;

            if (isAnnual) {
                const monthlyProduct = iapProducts.find(prod => {
                    const pid = prod.rcPackageIdentifier || "";
                    return pid.includes("monthly") && !pid.includes("annual") && (isPlatinum ? pid.includes("platinum") : !pid.includes("platinum"));
                });

                if (monthlyProduct && monthlyProduct.priceNumber && p.priceNumber) {
                    const monthlyPrice = monthlyProduct.priceNumber;
                    const annualPrice = p.priceNumber;
                    const yearlyCostIfMonthly = monthlyPrice * 12;

                    if (yearlyCostIfMonthly > annualPrice) {
                        originalPrice = p.currencyCode
                            ? new Intl.NumberFormat('en-US', { style: 'currency', currency: p.currencyCode }).format(yearlyCostIfMonthly)
                            : `$${yearlyCostIfMonthly.toFixed(2)}`;

                        const savings = ((yearlyCostIfMonthly - annualPrice) / yearlyCostIfMonthly) * 100;
                        discountPercent = `${Math.round(savings)}%`;
                    }
                }
            }

            return {
                id: p.productId,
                productId: p.productId,
                title: title,
                subtitle: isAnnual ? t("purchase.plan.annualSubtitle") : t("purchase.plan.monthlySubtitle"),
                price: normalizePriceString(p.price, p.currencyCode),
                originalPrice,
                discountPercent,
                promotion,
                features: isPlatinum ? tierFeatures.platinum : tierFeatures.pro,
                isBestValue: isAnnual,
                tier: tier,
                sortOrder: (isPlatinum ? 10 : 0) + (isAnnual ? 1 : 0),
                isAnnual,
            };
        }).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        return [freePlan, ...paidPlans].map(plan => ({
            ...plan,
            subtitle: plan.subtitle,
        }));
    }, [iapProducts, activeSubscriptionProductId, t]);

    useEffect(() => {
        if (activeSubscriptionProductId) {
            setSelectedPlanId(activeSubscriptionProductId);
            const activePlan = plans.find(p => p.id === activeSubscriptionProductId);
            if (activePlan?.tier === "platinum" || activePlan?.tier === "pro") {
                setSelectedTier(activePlan.tier);
            }
        } else if (plans.length > 1) {
            if (selectedPlanId === "free" && plans.length > 1) {
                // Keep free selected initially for now
            }
        }
    }, [activeSubscriptionProductId, plans.length]);

    useEffect(() => {
        if (selectedTier) {
            const next = plans.find(p => p.tier === selectedTier);
            if (next) {
                setSelectedPlanId(next.id);
            }
        }
    }, [selectedTier, plans]);


    const handlePurchase = async () => {
        const selectedPlan = plans.find((p) => p.id === selectedPlanId);
        if (!selectedPlan || !selectedPlan.productId) return;

        if (!iapReady) {
            setIapError(t("purchase.storeNotReady"));
            return;
        }

        try {
            setIsProcessing(true);
            setIapError(null);
            setIapStatus(t("purchase.processingPurchase"));

            const result = await purchaseProduct(selectedPlan.productId);

            const customerInfo = (result as any)?.customerInfo;
            const entitlements = customerInfo?.entitlements?.active;
            if (entitlements && Object.keys(entitlements).length > 0) {
                const activeEnt = Object.values(entitlements)[0] as any;
                if (activeEnt?.productIdentifier) {
                    setActiveSubscriptionProductId(activeEnt.productIdentifier);
                }
            }

            // If embedded, we might want to just proceed (call onContinue).
            // But standard behavior is showing success page. 
            // Let's stick to navigating to success page for now, as it provides good feedback.
            // The Success page can handle "Go to Home" logic.
            navigate("/purchase-success", {
                state: { product: iapProducts.find(p => p.productId === selectedPlan.productId) },
            });

        } catch (error) {
            console.error("Purchase failed", error);
            setIapError((error as Error)?.message || t("purchase.purchaseFailed"));
        } finally {
            setIsProcessing(false);
            setIapStatus(null);
        }
    };

    const handleUnsubscribe = () => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
            window.open('https://apps.apple.com/account/subscriptions', '_blank');
        } else {
            if (subscriptionManagementURL) {
                window.open(subscriptionManagementURL, '_blank');
            } else {
                window.open('https://play.google.com/store/account/subscriptions', '_blank');
            }
        }
    };

    const handleRestore = async () => {
        if (!iapReady) {
            setIapError(t("purchase.storeNotReady"));
            return;
        }

        try {
            setIsProcessing(true);
            setIapError(null);
            setIapStatus(t("purchase.restore"));

            const { customerInfo } = await Purchases.restorePurchases();

            if (customerInfo) {
                const entitlements = (customerInfo as any)?.entitlements?.active;
                if (entitlements && Object.keys(entitlements).length > 0) {
                    const activeEnt = Object.values(entitlements)[0] as any;
                    if (activeEnt?.productIdentifier) {
                        setActiveSubscriptionProductId(activeEnt.productIdentifier);
                        setIapStatus(t("purchase.restoreSuccess"));
                    } else {
                        setIapStatus(t("purchase.restoreNone"));
                    }
                } else {
                    setIapStatus(t("purchase.restoreNone"));
                }
            } else {
                setIapStatus(t("purchase.restoreNone"));
            }
        } catch (error) {
            console.error("Restore failed", error);
            setIapError(t("purchase.restoreFailed", { message: (error as Error)?.message || "Unknown error" }));
        } finally {
            setIsProcessing(false);
            setTimeout(() => setIapStatus(null), 3000);
        }
    };

    const hasActiveSubscription = activeSubscriptionProductId !== null && activeSubscriptionProductId !== "free";
    const selectedPlan = plans.find(p => p.id === selectedPlanId);
    const tierPlans = plans.filter(p => p.tier === selectedTier);
    const monthlyPlan = tierPlans.find(p => p.title.toLowerCase().includes(t("purchase.plan.monthlyLabel").toLowerCase())) || tierPlans.find(p => p.subtitle.toLowerCase().includes(t("purchase.plan.monthlySubtitle").toLowerCase()));
    const annualPlan = tierPlans.find(p => p.title.toLowerCase().includes(t("purchase.plan.annualLabel").toLowerCase())) || tierPlans.find(p => p.subtitle.toLowerCase().includes(t("purchase.plan.annualSubtitle").toLowerCase()));
    const tierIcon = selectedTier === "platinum" ? "/images/platinum_icon.png" : "/images/pro_icon.png";

    const textColor = isDark ? "text-white" : "text-gray-800";
    const textSecondary = isDark ? "text-white/70" : "text-gray-600";
    const textTertiary = isDark ? "text-white/80" : "text-gray-700";
    const textQuaternary = isDark ? "text-white/85" : "text-gray-800";

    return (
        <div className={`w-full h-full ${isEmbedded ? '' : 'fixed inset-0 overflow-hidden flex items-center justify-center px-6'}`}>
            {!isEmbedded && <div className={`absolute inset-0 bg-gradient-to-b ${getPageBackgroundGradient(theme)}`} />}

            {/* Back Button - Top Left (Only if not embedded) */}
            {!isEmbedded && (
                <div className="fixed top-16 left-4 z-50">
                    <button
                        onClick={onBack || (() => navigate("/profile"))}
                        className={`p-2 transition-colors`}
                    >
                        <ArrowLeft className={`w-6 h-6 ${isDark ? "text-white" : "text-gray-800"}`} strokeWidth={2.5} />
                    </button>
                </div>
            )}

            <div className={`relative z-10 h-full overflow-y-auto ${isEmbedded ? '' : 'pb-32'}`} style={{ paddingTop: isEmbedded ? '0' : '10vh' }}>

                <div className="md:w-full md:max-w-2xl mx-auto space-y-8 pb-4">

                    {/* Pricing Plans Title - above toggle */}
                    <div className="text-center">
                        <p className={`text-sm ${textSecondary}`}>{t("purchase.pricingPlans")}</p>
                    </div>

                    {/* Tier toggle */}
                    <div className="flex justify-center">
                        <div className={`inline-flex rounded-full p-1 gap-1 ${isDark ? "bg-white/10" : "bg-[#2B59FF]"}`}>
                            <button
                                onClick={() => setSelectedTier("pro")}
                                className={`px-4 py-2 text-sm font-semibold rounded-full transition-all ${selectedTier === "pro"
                                    ? isDark
                                        ? "bg-white text-[#0b3cba]"
                                        : "bg-[#C4D5FF] text-[#0b3cba]"
                                    : isDark
                                        ? "text-white/80"
                                        : "bg-transparent text-white"
                                    }`}
                            >
                                {t("purchase.plan.proShort")}
                            </button>
                            <button
                                onClick={() => setSelectedTier("platinum")}
                                className={`px-4 py-2 text-sm font-semibold rounded-full transition-all ${selectedTier === "platinum"
                                    ? isDark
                                        ? "bg-white text-[#0b3cba]"
                                        : "bg-[#C4D5FF] text-[#0b3cba]"
                                    : isDark
                                        ? "text-white/80"
                                        : "bg-transparent text-white"
                                    }`}
                            >
                                {t("purchase.plan.platinumShort")}
                            </button>
                        </div>
                    </div>

                    {/* Selected Plan Name - below toggle */}
                    <div className="text-center">
                        <h1 className={`text-3xl font-bold tracking-tight ${textColor}`}>
                            {selectedTier === "platinum" ? t("purchase.plan.platinum") : t("purchase.plan.pro")}
                        </h1>
                    </div>

                    {/* Icon */}
                    <div className="flex justify-center">
                        <img src={tierIcon} alt={selectedTier} className="w-20 h-20" />
                    </div>

                    {/* Subtitle and features */}
                    <div className="text-center space-y-3">
                        <p className={`text-lg ${textTertiary}`}>{t("purchase.heroHeadline")}</p>
                        <ul className={`space-y-2 ${textQuaternary} text-base max-w-sm mx-auto text-left`}>
                            {(selectedPlan?.features ?? []).map((feat, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                    <CheckCircle2 className={`h-4 w-4 ${isDark ? "text-emerald-400" : "text-emerald-600"} mt-1`} />
                                    <span>{feat}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Price cards */}
                    <div className="flex flex-row gap-4 flex-nowrap pb-32">
                        {[monthlyPlan, annualPlan].filter(Boolean).map((plan) => {
                            const isCurrentPlan = activeSubscriptionProductId
                                ? activeSubscriptionProductId === plan!.id
                                : false;
                            return (
                                <button
                                    key={plan!.id}
                                    onClick={() => setSelectedPlanId(plan!.id)}
                                    className={`relative flex-1 min-w-0 rounded-3xl border-2 ${selectedPlanId === plan!.id
                                        ? isDark
                                            ? "border-white/80"
                                            : "border-[#FFF000]"
                                        : isDark
                                            ? "border-white/20"
                                            : "border-[#FFF000]/50"
                                        } ${isDark ? "bg-gradient-to-b from-[#0b3cba] to-[#0b2f8c]" : "bg-[#2B59FF]"} px-4 py-6 text-left shadow-lg transition-transform active:scale-95`}
                                >
                                    {isCurrentPlan ? (
                                        <div className={`absolute -top-3 right-4 ${isDark ? "bg-white/70 text-[#0b2f8c]" : "bg-white text-[#2B59FF]"} text-[11px] font-semibold px-3 py-1 rounded-full shadow`}>
                                            {t("purchase.currentBadge")}
                                        </div>
                                    ) : plan!.discountPercent ? (
                                        <div className="absolute -top-3 right-4 bg-[#FFF000] text-[#1a1200] text-xs font-semibold px-3 py-1 rounded-full shadow">
                                            {plan!.discountPercent} {t("purchase.off")}
                                        </div>
                                    ) : null}

                                    <div className="space-y-1">
                                        <p className={`text-sm ${isDark ? "text-white/90" : "text-white"}`}>{plan!.subtitle}</p>
                                    </div>
                                    {plan!.originalPrice && (
                                        <p className={`text-sm ${isDark ? "text-white/70" : "text-white/80"} line-through mb-1`}>{plan!.originalPrice}</p>
                                    )}
                                    <p className={`text-xl sm:text-2xl md:text-3xl font-bold ${isDark ? "text-white" : "text-white"} whitespace-nowrap tracking-tight`}>{plan!.price}</p>
                                </button>
                            );
                        })}
                    </div>

                </div>
            </div>

            {/* Sticky CTA Button at Bottom */}
            <div
                className={`fixed bottom-0 left-0 right-0 z-20 px-6 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] backdrop-blur-sm border-t ${isDark ? "border-white/10" : "border-gray-200"}`}
                style={{
                    backgroundColor: isDark
                        ? "rgba(10, 91, 218, 0.95)"
                        : "rgba(204, 229, 255, 0.95)"
                }}
            >
                <div className="md:w-full md:max-w-2xl mx-auto space-y-3">
                    {/* Or continue with free (place above Subscribe) */}
                    {!hasActiveSubscription && (
                        <button
                            onClick={onContinue || (() => navigate("/profile"))}
                            className={`w-full min-h-[48px] py-3 rounded-xl text-center text-lg font-extrabold ${isDark ? "bg-white/10 text-white hover:bg-white/15" : "bg-black/5 text-gray-900 hover:bg-black/10"} transition-colors`}
                        >
                            {t("purchase.continueFree")}
                        </button>
                    )}

                    {hasActiveSubscription ? (
                        <button
                            onClick={handleUnsubscribe}
                            className="w-full rounded-2xl bg-gradient-to-r from-[#ffe898] to-[#ffd86b] py-3.5 text-lg font-bold text-[#1a1200] shadow-lg transition-transform active:scale-95 hover:brightness-110"
                        >
                            {t("purchase.manage")}
                        </button>
                    ) : (
                        <button
                            onClick={handlePurchase}
                            disabled={isProcessing || !iapReady || selectedPlanId === "free"}
                            className={`w-full rounded-2xl bg-gradient-to-r from-[#ffe898] to-[#ffd86b] py-3.5 text-lg font-bold text-[#1a1200] shadow-lg transition-transform active:scale-95 ${(isProcessing || !iapReady || selectedPlanId === "free") ? "opacity-50 cursor-not-allowed" : "hover:brightness-110"
                                }`}
                        >
                            {isProcessing ? (
                                <span className="flex items-center justify-center gap-2"><Loader2 className="animate-spin h-5 w-5" /> {t("purchase.processing")}</span>
                            ) : (() => {
                                if (selectedPlan?.promotion) {
                                    return t("purchase.startPromo", { promo: selectedPlan.promotion.description });
                                }
                                return t("purchase.subscribe");
                            })()}
                        </button>
                    )}

                    {(iapStatus || iapError) && (
                        <p className={`text-center text-xs ${iapError ? (isDark ? "text-red-400" : "text-red-600") : (isDark ? "text-emerald-400" : "text-emerald-600")}`}>
                            {iapError || iapStatus}
                        </p>
                    )}

                    {hasActiveSubscription ? (
                        <div className="flex gap-3">
                            {onContinue ? (
                                <button
                                    onClick={onContinue}
                                    className={`flex-1 text-center text-sm ${isDark ? "text-white/70 hover:text-white" : "text-gray-600 hover:text-gray-800"} transition`}
                                >
                                    {t("purchase.continueCurrent")}
                                </button>
                            ) : (
                                <button
                                    onClick={handleUnsubscribe}
                                    className={`flex-1 text-center text-sm ${isDark ? "text-white/70 hover:text-white" : "text-gray-600 hover:text-gray-800"} transition`}
                                >
                                    {t("purchase.unsubscribe")}
                                </button>
                            )}
                            <button
                                onClick={handleRestore}
                                disabled={isProcessing}
                                className={`flex-1 text-center text-sm ${isDark ? "text-white/70 hover:text-white" : "text-gray-600 hover:text-gray-800"} transition ${isProcessing ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                {t("purchase.restore")}
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleRestore}
                            disabled={isProcessing}
                            className={`w-full text-center text-sm ${isDark ? "text-white/70 hover:text-white" : "text-gray-600 hover:text-gray-800"} transition ${isProcessing ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                            {t("purchase.restore")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
